import React from 'react';
import { Modal, Form, Button, Spinner } from 'react-bootstrap';
import { BsCloudUpload } from 'react-icons/bs';

interface CandidatoBancaResponse {
    candidatoId: string;
    nome: string;
    nomeSocial: string;
}

interface VideoUploadModalProps {
    show: boolean;
    onHide: () => void;
    selectedCandidato: CandidatoBancaResponse | null;
    videoFile: File | null;
    customFileName: string;
    uploading: boolean;
    loadingSuggestion: boolean;
    onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onFileNameChange: (value: string) => void;
    onUpload: () => void;
}

const ColoredFileName: React.FC<{ fileName: string }> = ({ fileName }) => {
    const parts = fileName.split('_');
    const colors = ['#0d6efd', '#198754', '#dc3545', '#fd7e14'];
    let domainIndex = 0;
    let partIndex = 0;
    
    const isDatePart = (part: string) => /^\d{2}-\d{2}-\d{2}$/.test(part);
    
    return (
        <>
            {parts.map((part, idx) => {
                if (idx > 0 && isDatePart(parts[idx - 1]) && domainIndex === 2) {
                    domainIndex = 3;
                    partIndex = 0;
                } else if (idx > 0 && isDatePart(part)) {
                    domainIndex = 2;
                    partIndex = 0;
                } else if (idx > 0 && !isDatePart(part) && !isDatePart(parts[idx - 1])) {
                    partIndex++;
                    if (domainIndex === 0 && partIndex >= 2) {
                        domainIndex = 1;
                        partIndex = 0;
                    }
                }
                
                return (
                    <span key={idx}>
                        <span style={{ color: colors[domainIndex], fontWeight: '500' }}>{part}</span>
                        {idx < parts.length - 1 && <span style={{ color: '#6c757d' }}>_</span>}
                    </span>
                );
            })}
        </>
    );
};

const VideoUploadModal: React.FC<VideoUploadModalProps> = ({
    show,
    onHide,
    selectedCandidato,
    videoFile,
    customFileName,
    uploading,
    loadingSuggestion,
    onFileChange,
    onFileNameChange,
    onUpload
}) => {
    return (
        <Modal show={show} onHide={onHide} centered size="lg">
            <Modal.Header closeButton className="bg-primary text-white">
                <Modal.Title>
                    <BsCloudUpload className="me-2" />
                    Upload de Vídeo
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-4">
                <div className="mb-3">
                    <h6 className="text-muted mb-2">Candidato:</h6>
                    <p className="fw-bold mb-3">{selectedCandidato?.nome}</p>
                </div>
                <Form>
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold">Arquivo de Vídeo</Form.Label>
                        <Form.Control 
                            type="file" 
                            accept="video/mp4,video/avi,video/mov,video/wmv"
                            onChange={onFileChange}
                            disabled={uploading}
                            className="form-control-lg"
                        />
                        <Form.Text className="text-muted">
                            <small>Formato aceito: MP4</small>
                        </Form.Text>
                    </Form.Group>
                    
                    {videoFile && (
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-semibold">
                                Nome do Arquivo {loadingSuggestion && <Spinner size="sm" />}
                            </Form.Label>
                            <div className="mb-2 p-2 bg-light rounded" style={{ fontFamily: 'monospace', fontSize: '0.9em', wordBreak: 'break-all' }}>
                                <ColoredFileName fileName={customFileName} />
                            </div>
                            <Form.Control
                                type="text"
                                value={customFileName}
                                onChange={(e) => onFileNameChange(e.target.value)}
                                disabled={uploading || loadingSuggestion}
                                maxLength={255}
                                placeholder="Digite o nome do arquivo"
                                style={{ fontFamily: 'monospace' }}
                            />
                            <Form.Text className={customFileName.length > 255 ? "text-danger" : "text-muted"}>
                                <small>{customFileName.length}/255 caracteres</small>
                            </Form.Text>
                        </Form.Group>
                    )}
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide} disabled={uploading}>
                    Cancelar
                </Button>
                <Button variant="primary" onClick={onUpload} disabled={!videoFile || uploading}>
                    {uploading ? <Spinner size="sm" /> : 'Enviar Vídeo'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default VideoUploadModal;
